import { jwtDecode } from "jwt-decode";
import { sentryLog } from "@/utils/sentry-utils";

const TOKEN_STORAGE_KEY = "usr_token";

/**
 * Response header carrying a session token re-signed with the server's current
 * key. Mirrors `REISSUED_TOKEN_HEADER` in `server/src/lib/token-rotation.ts`.
 */
const REISSUED_TOKEN_HEADER = "x-reissued-token";

const JWT_SHAPE = /^[\w-]+\.[\w-]+\.[\w-]+$/;

/**
 * Strips the surrounding quotes a JSON-serialized token carries, tolerating a
 * missing value. Returns "" when the token is null/undefined instead of
 * throwing — the legacy call sites did `token.replaceAll('"', "")` directly and
 * crashed with "null is not an object" whenever the token was absent (Sentry
 * LIME-CLIENT-2F / R8 / R9 / RC).
 */
export function cleanToken(token: string | null | undefined): string {
  return (token ?? "").replaceAll('"', "");
}

/**
 * Reads the access token from localStorage and returns it cleaned, defaulting
 * to "" when the token is missing or storage is unavailable (Safari private
 * mode, cleared storage). A blank token simply yields a request the server
 * rejects with 401, which the response interceptor turns into a logout — far
 * better than an uncaught TypeError.
 */
export function getCleanAccessToken(): string {
  try {
    return cleanToken(localStorage.getItem(TOKEN_STORAGE_KEY));
  } catch {
    return "";
  }
}

/**
 * The client the session belongs to, or null when there is no readable token.
 *
 * Used to scope per-account browser storage: ids like a location's are unique
 * only within a client, so a key built from one alone is shared by every
 * account that signs in on the same browser.
 */
export function getSessionClientId(): number | null {
  const token = getCleanAccessToken();
  if (!token) return null;

  try {
    const { clientId } = jwtDecode<{ clientId?: number }>(token);
    return typeof clientId === "number" ? clientId : null;
  } catch {
    return null;
  }
}

// Writes/clears the token defensively: some Safari contexts (private mode,
// sandboxed iframes) leave `localStorage` undefined, so an unguarded access
// throws "Can't find variable: localStorage" (Sentry LIME-CLIENT-11V / DEV-9871).
export function setAccessToken(token: string): void {
  try {
    localStorage.setItem(TOKEN_STORAGE_KEY, token);
  } catch {
    // Storage unavailable; nothing to persist.
  }
}

export function clearAccessToken(): void {
  try {
    localStorage.removeItem(TOKEN_STORAGE_KEY);
  } catch {
    // Storage unavailable; nothing to clear.
  }
}

// Recovers the isAuth/token desync that leaves the app firing tokenless 403s
// after the token is wiped mid-session — 403 isn't the 401/498 the response
// interceptors recover from, so nothing else routes back to login (DEV-8683).
export function forceRelogin(reason: string): void {
  sentryLog.warn("Force relogin - empty access token", { reason });
  clearAccessToken();
  window.location.assign("/login");
}

/**
 * Adopts a session token the server re-signed with its current key, so a
 * TOKEN_KEY rotation migrates active sessions silently instead of logging
 * everyone out when the previous key is retired (DEV-9694).
 *
 * Shape-checked before it is stored: the value ends up on every subsequent
 * request, and anything that is not a JWT would only produce 401s.
 */
export function adoptReissuedToken(headers: unknown): void {
  if (typeof headers !== "object" || headers === null) return;

  const reissued = (headers as Record<string, unknown>)[REISSUED_TOKEN_HEADER];

  if (typeof reissued !== "string" || !JWT_SHAPE.test(reissued)) return;

  setAccessToken(reissued);
}
